import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, MessageBar, MessageBarBody, Switch, Tab, TabList, Text, tokens } from "@fluentui/react-components";
import { ChevronLeft20Regular, ChevronRight20Regular } from "@fluentui/react-icons";
import { addDays, format, startOfWeek } from "date-fns";
import { CELLS_PER_DAY, CELL_MIN, GRID_START_MIN, cellsChanged, cellsToSlots, emptyCells, slotsToCells, toMin, type Cells, type Weekday } from "@gymplan/shared";
import { api, type AvailabilitySlot, type Client } from "../lib/api.ts";
import { AvailabilityGrid, type CellMark } from "../components/AvailabilityGrid.tsx";
import { ChangeHours } from "../components/ChangeHours.tsx";

/**
 * Direct edit of availability: the usual week (template) or one specific week.
 * Tap or drag on the grid; the running summary under it says what you've set.
 */
export function AvailabilityPage() {
  const qc = useQueryClient();
  const [mode, setMode] = useState<"template" | "week">("template");
  const [week, setWeek] = useState(() => startOfWeek(new Date(), { weekStartsOn: 1 }));
  const weekYmd = format(week, "yyyy-MM-dd");
  const qKey = mode === "week" ? ["availability", weekYmd] : ["availability"];
  const qUrl = mode === "week" ? `/api/trainers/me/availability?week=${weekYmd}` : "/api/trainers/me/availability";
  const q = useQuery({ queryKey: qKey, queryFn: () => api.get<{ source: "template" | "week"; slots: AvailabilitySlot[] }>(qUrl) });
  const clients = useQuery({ queryKey: ["clients"], queryFn: () => api.get<Client[]>("/api/clients") });

  const saved = useMemo(() => (q.data ? slotsToCells(q.data.slots) : null), [q.data]);
  const [cells, setCells] = useState<Cells>(emptyCells);
  const [showPrefs, setShowPrefs] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  useEffect(() => {
    if (saved) setCells(saved);
  }, [saved]);
  const dirty = !!saved && cellsChanged(saved, cells) > 0;

  const save = useMutation({
    mutationFn: () => api.put(qUrl, { slots: cellsToSlots(cells).map((x) => ({ ...x, gymId: null })) }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["availability"] });
      void qc.invalidateQueries({ queryKey: ["schedule"] });
    },
  });
  const resetWeek = useMutation({
    mutationFn: () => api.del(`/api/trainers/me/availability?week=${weekYmd}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["availability"] });
      void qc.invalidateQueries({ queryKey: ["schedule"] });
    },
  });

  const guard = (fn: () => void) => () => {
    if (dirty && !confirm("Discard unsaved changes?")) return;
    fn();
  };

  // Client preferences as a light marker on the cells they cover (context only).
  const marks = useMemo(() => {
    if (!showPrefs) return undefined;
    const m: Partial<Record<Weekday, (CellMark | null)[]>> = {};
    for (const c of clients.data ?? []) {
      for (const p of c.preferences) {
        const d = p.weekday as Weekday;
        const a = p.startTime ? toMin(p.startTime) : GRID_START_MIN;
        const b = p.endTime ? toMin(p.endTime) : GRID_START_MIN + CELLS_PER_DAY * CELL_MIN;
        m[d] ??= Array<CellMark | null>(CELLS_PER_DAY).fill(null);
        for (let i = 0; i < CELLS_PER_DAY; i++) {
          const cs = GRID_START_MIN + i * CELL_MIN;
          if (cs < b && cs + CELL_MIN > a) m[d]![i] = "pref";
        }
      }
    }
    return m;
  }, [showPrefs, clients.data]);

  return (
    <div>
      <Text as="h1" size={800} weight="semibold" block>
        Availability
      </Text>
      <Text block style={{ color: tokens.colorNeutralForeground3, marginBottom: 12 }}>
        The hours you can train clients. RackUp only offers clients times inside these.
      </Text>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12, flexWrap: "wrap" }}>
        <TabList selectedValue={mode} onTabSelect={(_, v) => guard(() => setMode(v.value as "template" | "week"))()}>
          <Tab value="template">Every week</Tab>
          <Tab value="week">A specific week</Tab>
        </TabList>
        {mode === "week" && (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
            <Button appearance="subtle" icon={<ChevronLeft20Regular />} aria-label="Previous week" onClick={guard(() => setWeek(addDays(week, -7)))} />
            <Text weight="semibold">w/c {format(week, "d MMM yyyy")}</Text>
            <Button appearance="subtle" icon={<ChevronRight20Regular />} aria-label="Next week" onClick={guard(() => setWeek(addDays(week, 7)))} />
          </span>
        )}
        <div style={{ flex: 1 }} />
        <Switch label="Show client preferences" checked={showPrefs} onChange={(_, d) => setShowPrefs(d.checked)} />
      </div>
      {mode === "week" && q.data && (
        <Text size={200} block style={{ color: tokens.colorNeutralForeground3, marginBottom: 8 }}>
          {q.data.source === "week" ? "This week has its own hours." : "This week uses your usual hours — change and save to customise just this week."}
          {q.data.source === "week" && (
            <Button size="small" appearance="subtle" style={{ marginLeft: 8 }} onClick={() => confirm("Go back to your usual hours for this week?") && resetWeek.mutate()}>
              Use usual hours
            </Button>
          )}
        </Text>
      )}

      {saved && !dirty && (
        <ChangeHours key={`${mode}-${weekYmd}`} current={saved} onPreview={setPreviewing} putUrl={qUrl} interpretUrl={`/api/trainers/me/availability/interpret${mode === "week" ? `?week=${weekYmd}` : ""}`} />
      )}
      {!previewing && (
      <AvailabilityGrid
        value={cells}
        onChange={setCells}
        marks={marks}
        actions={
          <>
            {dirty && <Button appearance="subtle" onClick={() => saved && setCells(saved)}>Undo changes</Button>}
            <Button appearance="primary" disabled={!dirty || save.isPending} onClick={() => save.mutate()}>
              {save.isPending ? "Saving…" : "Save"}
            </Button>
          </>
        }
      />
      )}
      {save.isError && (
        <MessageBar intent="error" style={{ marginTop: 8 }}>
          <MessageBarBody>{(save.error as Error).message}</MessageBarBody>
        </MessageBar>
      )}
      {save.isSuccess && !dirty && (
        <Text size={200} block style={{ marginTop: 8, color: tokens.colorPaletteGreenForeground1 }}>Saved.</Text>
      )}
    </div>
  );
}
